import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, ChevronDown, ChevronRight, Zap, LayersIcon, Users, ArrowRight, Quote } from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY, OPPORTUNITY_COLORS, fmt } from "./shared";
import { COVERAGE_COLORS, Chip, ClusterArticles, PriorityMetrics } from "./TopicParts";

// Search topics = topic clusters built from GSC queries by Gap Analysis. Status comes from
// matched content (article cluster or Business Context key page) or, with neither, from
// whether the topic's queries already rank on the site — so it works with no CMS.
const STATUS = {
  gap:     { label: "No content", bg: "#FFF1F2", text: "#BE123C", help: "People search this topic, but no page of yours ranks or covers it. Write a pillar page." },
  weak:    { label: "Weak",       bg: "#FFFBEB", text: "#B45309", help: "You have a page for it, but it ranks past page 1. Strengthen or expand it." },
  covered: { label: "Covered",    bg: "#ECFDF5", text: "#065F46", help: "Content exists and ranks on page 1. Keep it fresh." },
};
const STATUS_ORDER = ["gap", "weak", "covered"];

// Lead potential (query_topics.lead_tier): buyers x buying stage x demand.
const TIERS = {
  high:     { label: "High priority",   bg: "#018D4C", text: "#FFFFFF" },
  medium:   { label: "Medium priority", bg: "#E4E4E7", text: "#27272A" },
  low:      { label: "Low priority",    bg: "#F4F4F5", text: "#A1A1AA" },
  unscored: { label: "Not scored",      bg: "#FFFFFF", text: "#A1A1AA" },
};
const TIER_ORDER = ["high", "medium", "low", "unscored"];
const SEGMENT_ON = { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" };
const SEGMENT_OFF = { color: "#71717a" };

// Jev's next move per topic (topic_judgments.NEXT_MOVES), chosen after classification.
const NEXT_MOVES = {
  write_new:      { label: "Write new page",     help: "No page serves this topic yet: write a pillar page." },
  expand_page:    { label: "Expand page",        help: "A page exists but ranks past page 1: expand or restructure it." },
  fix_snippet:    { label: "Fix title & meta",   help: "Ranks near page 1 but few clicks: rewrite titles and meta descriptions." },
  consolidate:    { label: "Merge pages",        help: "Several pages compete for the same searches: merge into one." },
  internal_links: { label: "Add internal links", help: "A good page ranks just below the top: link to it from related pages." },
  maintain:       { label: "Keep fresh",         help: "Already ranks well and earns clicks: keep it up to date." },
  deprioritize:   { label: "Deprioritize",       help: "Most searchers aren't your buyers: don't invest more." },
};

// Same verdicts as the Audience page's problem segments (these topics are those segments).
const AUDIENCE = {
  strengthen: { label: "Your buyers",  bg: "#ECFDF5", text: "#065F46" },
  bridge:     { label: "Early stage",  bg: "#FFFBEB", text: "#B45309" },
  prune:      { label: "Not your buyer", bg: "#FEF2F2", text: "#DC2626" },
};
const AWARENESS = {
  unaware: "Unaware", problem_aware: "Problem-aware", solution_aware: "Solution-aware",
  product_aware: "Product-aware", most_aware: "Most aware",
};

const pathOf = url => {
  try { return new URL(url).pathname || "/"; } catch { return url; }
};

function TopicQueries({ topicId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.getPerformanceQueries({ query_topic_id: topicId, sort: "impressions", order: "desc", limit: 15 })
      .then(r => setRows(r.queries || []))
      .catch(() => setRows([]));
  }, [topicId]);

  return (
    <div>
      <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Search demand</p>
      {rows === null ? (
        <p className="text-xs text-zinc-400 py-2">Loading queries…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-zinc-400 py-2">No queries in this topic.</p>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {rows.map(q => {
            const oc = OPPORTUNITY_COLORS[q.opportunity];
            return (
              <button
                key={q.id}
                onClick={() => navigate(`/dashboard/query-gaps/${q.id}`)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-100 hover:border-zinc-300 rounded-lg text-left"
              >
                <span className="text-xs text-zinc-900 truncate flex-1">{q.query}</span>
                <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
                  {fmt(q.impressions)} impr · pos {fmt(q.position, 1)}
                </span>
                {oc && <Chip bg={oc.bg} text={oc.text}>{q.opportunity.replace(/_/g, " ")}</Chip>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TopicRow({ topic, hasCms, onFanOut, onTrackAi, focused }) {
  const [expanded, setExpanded] = useState(!!focused);
  const ref = useRef(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focused]);
  const st = STATUS[topic.coverage_status];
  const move = NEXT_MOVES[topic.next_move];
  const tier = TIERS[topic.lead_tier];
  const aud = AUDIENCE[topic.audience_verdict];
  const grade = COVERAGE_COLORS[topic.matched_cluster_grade];
  const optimize = topic.underperforming_queries - topic.quick_wins;

  return (
    <div ref={ref} id={`topic-${topic.id}`} className={`border-b border-zinc-100 last:border-0 ${focused ? "ring-2 ring-inset ring-emerald-200" : ""}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setExpanded(v => !v))}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 hover:bg-zinc-50 cursor-pointer"
      >
        <div className="min-w-0 flex-1 basis-60">
          <p className="text-sm font-bold text-zinc-900 truncate">{topic.topic}</p>
          {topic.problem && <p className="text-[11px] text-zinc-500 mt-0.5 truncate" title={topic.problem}>{topic.problem}</p>}
          <p className="text-[11px] text-zinc-400 mt-0.5 truncate">
            {fmt(topic.queries)} queries
            {topic.avg_position != null && <> · avg pos {fmt(topic.avg_position, 1)}</>}
            {topic.matched_cluster_label && (
              <> · content: <span className="text-zinc-600 font-bold">{topic.matched_cluster_label}</span> ({fmt(topic.matched_cluster_size)} articles)</>
            )}
            {topic.matched_key_page && (
              <> · key page: <span className="text-zinc-600 font-bold">{pathOf(topic.matched_key_page)}</span></>
            )}
          </p>
        </div>

        <div className="flex items-center gap-4 text-right tabular-nums">
          <PriorityMetrics priority={topic.priority} impressions={topic.impressions} clicks={topic.clicks} />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap justify-end basis-56">
          {tier && (
            <Chip bg={tier.bg} text={tier.text} title={`Lead potential: ${(topic.lead_reasons || []).join(" · ")}`}>
              {tier.label}
            </Chip>
          )}
          {move && (
            <Chip bg="#18181B" text="#FFFFFF" title={`Next move (Jev, ${Math.round((topic.next_move_confidence || 0) * 100)}% confident): ${move.help}`}>
              → {move.label}
            </Chip>
          )}
          {st && <Chip bg={st.bg} text={st.text} title={st.help}>{st.label}</Chip>}
          {aud && (
            <Link
              to={`/dashboard/audience#seg-${topic.id}`}
              onClick={e => e.stopPropagation()}
              title={`Audience: buyer fit ${topic.buyer_fit?.toFixed(2) ?? "—"}${topic.dominant_awareness ? ` · ${AWARENESS[topic.dominant_awareness] || topic.dominant_awareness}` : ""}. Open this segment on the Audience page.`}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-full whitespace-nowrap hover:opacity-80"
              style={{ background: aud.bg, color: aud.text }}
            >
              <Users size={9} /> {aud.label}
            </Link>
          )}
          {topic.missing_queries > 0 && (
            <Chip {...OPPORTUNITY_COLORS.missing_coverage} title="Queries with impressions but no ranking page">
              {topic.missing_queries} to write
            </Chip>
          )}
          {topic.quick_wins > 0 && (
            <Chip {...OPPORTUNITY_COLORS.quick_win} title="Underperforming queries at position 11–30">
              {topic.quick_wins} quick wins
            </Chip>
          )}
          {optimize > 0 && (
            <Chip {...OPPORTUNITY_COLORS.growth_opportunity} title="Underperforming queries past position 30">
              {optimize} to optimize
            </Chip>
          )}
          {grade && <Chip bg={grade.bg} text={grade.text} title="Matched cluster's content depth grade">{topic.matched_cluster_grade}</Chip>}
          <button
            onClick={e => { e.stopPropagation(); onFanOut(topic.topic); }}
            title="Run query fan-out for this topic"
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border hover:opacity-80"
            style={{ background: "#ECFDF5", color: PRIMARY, borderColor: "#A7F3D0" }}
          >
            <Zap size={10} /> Fan-Out
          </button>
          <button
            onClick={e => { e.stopPropagation(); onTrackAi(topic.id); }}
            title="Track this topic's searches in Google AI Overviews"
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
          >
            <Quote size={10} /> Track in AI
          </button>
          {expanded ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
          {move ? (
            <p className="text-[11px] text-zinc-800 mb-2">
              <strong>Next move: {move.label}.</strong> {move.help}
              <span className="text-zinc-400"> Chosen by Jev, {Math.round((topic.next_move_confidence || 0) * 100)}% confident.</span>
            </p>
          ) : st && <p className="text-[11px] mb-2" style={{ color: st.text }}><strong>{st.label}.</strong> {st.help}</p>}
          {aud && (
            <p className="text-[11px] text-zinc-500 mb-4">
              <span className="text-zinc-400">Who searches:</span>{" "}
              {[AWARENESS[topic.dominant_awareness], topic.dominant_role, topic.dominant_persona].filter(Boolean).join(" · ") || "—"}
              {" "}· buyer fit {topic.buyer_fit?.toFixed(2) ?? "—"}{" "}
              <Link to={`/dashboard/audience#seg-${topic.id}`} className="font-bold hover:underline" style={{ color: PRIMARY }}>
                View in Audience <ArrowRight size={10} className="inline" />
              </Link>
            </p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopicQueries topicId={topic.id} />
            {topic.matched_cluster_id ? (
              <ClusterArticles clusterId={topic.matched_cluster_id} />
            ) : (
              <div>
                <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Existing coverage</p>
                {topic.matched_key_page ? (
                  <a href={topic.matched_key_page} target="_blank" rel="noopener noreferrer"
                    className="block px-3 py-2 bg-white border border-zinc-100 hover:border-zinc-300 rounded-lg text-xs text-zinc-900 truncate">
                    Key page: {pathOf(topic.matched_key_page)}
                  </a>
                ) : (
                  <p className="text-xs text-zinc-400 py-2">
                    {hasCms ? "No article cluster or key page matches this topic." : "No key page matches this topic. Connect a CMS to compare against your articles."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SearchTopics({ onGoToQueries }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  // Set by openSearchTopic (e.g. from the Audience page): expand + scroll to that topic once.
  const [focusId] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("sp:topics.focus") || "null");
      localStorage.removeItem("sp:topics.focus");
      return v;
    } catch { return null; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setData(await api.getQueryTopics({ limit: 100 }).catch(e => { console.error(e); return null; }));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const topics = data?.topics || [];
  const hasCms = !!data?.has_cms;
  const shown = topics
    .filter(t => tierFilter === "all" || t.lead_tier === tierFilter)
    .filter(t => filter === "all" || t.coverage_status === filter);
  const onFanOut = label => navigate(`/dashboard/fanout?seed=${encodeURIComponent(label)}`);
  const onTrackAi = id => navigate(`/dashboard/ai-citations?topic=${encodeURIComponent(id)}`);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        {topics.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5" title="Lead potential: buyers × buying stage × demand">
            {["all", ...TIER_ORDER].map(key => {
              const count = key === "all" ? topics.length : data.lead_tier_counts?.[key] || 0;
              if (key !== "all" && count === 0) return null;
              return (
                <button key={key} onClick={() => setTierFilter(key)}
                  className="px-2.5 py-1 text-[10px] font-bold rounded-md whitespace-nowrap"
                  style={tierFilter === key ? SEGMENT_ON : SEGMENT_OFF}>
                  {key === "all" ? "All priorities" : TIERS[key].label.replace(" priority", "")} ({count})
                </button>
              );
            })}
          </div>
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {["all", ...STATUS_ORDER].map(key => {
              const count = key === "all" ? topics.length : data.coverage_counts?.[key] || 0;
              if (key !== "all" && count === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className="px-2.5 py-1 text-[10px] font-bold rounded-md whitespace-nowrap"
                  style={filter === key
                    ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                    : { color: "#71717a" }}
                >
                  {key === "all" ? "All" : STATUS[key].label} ({count})
                </button>
              );
            })}
          </div>
          </div>
        ) : (
          <p className="text-xs text-zinc-400">
            {data ? `${data.total_topics} search topics · grouped from your top queries, ranked by lead potential` : "Loading…"}
          </p>
        )}
        <button onClick={load} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors" title="Reload">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {!loading && topics.length > 0 && !hasCms && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 mb-3 bg-blue-50 rounded-xl text-xs text-blue-800">
          <LayersIcon size={13} />
          <span>Statuses use your Search Console rankings and Business Context key pages. Connect a CMS to also compare against every article.</span>
          <a href="/dashboard/settings/cms" className="ml-auto font-bold hover:underline">Connect CMS →</a>
        </div>
      )}

      {loading && !data && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">Loading…</div>
      )}

      {!loading && topics.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">
          No search topics yet. Gap Analysis groups your top queries into topics.{" "}
          <button onClick={onGoToQueries} className="font-bold hover:underline" style={{ color: PRIMARY }}>Run it on the Queries tab →</button>
        </div>
      )}

      {shown.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm mb-3">
          {shown.map(t => <TopicRow key={t.id} topic={t} hasCms={hasCms} onFanOut={onFanOut} onTrackAi={onTrackAi} focused={t.id === focusId} />)}
        </div>
      )}

      {data?.no_demand_clusters?.length > 0 && filter === "all" && (
        <div className="px-4 py-3 bg-white rounded-xl border border-zinc-100">
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-1">Content with no search demand</p>
          <p className="text-[11px] text-zinc-400 mb-2">
            Article clusters no search topic maps to. Consolidate them, or Fan-Out to find what people search around them.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.no_demand_clusters.map(c => (
              <button
                key={c.cluster_id}
                onClick={() => navigate(`/dashboard/articles?cluster=${encodeURIComponent(c.cluster_id)}`)}
                className="px-2.5 py-1 text-[11px] rounded-full border border-zinc-200 text-zinc-600 hover:border-zinc-400"
              >
                {c.label || c.cluster_id} · {fmt(c.size)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
